import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Checkbox, Form, Input, InputNumber, Select } from "antd";
import dayjs from "dayjs";
import {
  createHealthClientUsl,
  fetchHealthClient,
  fetchSprSotr,
  fetchSprTov,
  updateHealthClientUsl
} from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const VID_PAN_OPTIONS = [
  { value: 0, label: "Не планировать" },
  { value: 1, label: "Каждый день" },
  { value: 2, label: "Через день" },
  { value: 7, label: "Раз в неделю" },
  { value: 12, label: "Через день (сквозное)" },
  { value: 100, label: "По времени" }
];

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const daysBetween = (beg, end) => {
  if (!beg || !end) return null;
  const a = dayjs(beg).startOf("day");
  const b = dayjs(end).startOf("day");
  if (!a.isValid() || !b.isValid()) return null;
  return b.diff(a, "day") + 1;
};

const normalizeTov = (row) => ({
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  name: pick(row, "name", "Name") ?? "",
  art: pick(row, "art", "Art") ?? "",
  zenaExp: toNumber(pick(row, "zenaExp", "ZenaExp")),
  vidPlan: toNumber(pick(row, "vidPlan", "VidPlan")),
  isPlat: toNumber(pick(row, "isPlat", "IsPlat"))
});

const normalizeSotr = (row) => ({
  idSotr: toNumber(pick(row, "idSotr", "IdSotr")),
  name: pick(row, "name", "Name") ?? ""
});

/**
 * Форма добавления/редактирования назначения (Delphi TFrmEPredNazn без Параметров).
 * embedded — без заголовка и кнопок (кнопки в drawer).
 */
const ClientAppointmentForm = ({
  idClient,
  item,
  form: formProp,
  embedded = false,
  onCancel,
  onSaved,
  onSavingChange
}) => {
  const [innerForm] = Form.useForm();
  const form = formProp || innerForm;
  const userId = useSelector((state) => state.user.session?.userId);
  const law = useSelector((state) => state.user.session?.law);
  const canEditPrice = getLawFlag(law, 100) === 1;
  const canEditDiscount = getLawFlag(law, 104) === 1;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tovOptions, setTovOptions] = useState([]);
  const [tovLoading, setTovLoading] = useState(false);
  const [sotrOptions, setSotrOptions] = useState([]);
  const [sotrLoading, setSotrLoading] = useState(false);
  const tovSearchRef = useRef(0);
  const tovTimerRef = useRef(0);
  const isEdit = Boolean(item?.idStr);

  const setSavingState = (value) => {
    setSaving(value);
    onSavingChange?.(value);
  };

  const canChangeUsl = !isEdit || (toNumber(item?.cntPlan) ?? 0) === 0;

  const sotrSelectOptions = useMemo(
    () =>
      sotrOptions
        .filter((s) => s.idSotr != null)
        .map((s) => ({ value: s.idSotr, label: s.name || `#${s.idSotr}` })),
    [sotrOptions]
  );

  const tovSelectOptions = useMemo(() => {
    const opts = tovOptions
      .filter((t) => t.idTov != null)
      .map((t) => ({
        value: t.idTov,
        label: t.art ? `${t.name} (${t.art})` : t.name,
        tov: t
      }));

    if (isEdit && item?.idUsl != null && !opts.some((o) => o.value === item.idUsl)) {
      opts.unshift({
        value: item.idUsl,
        label: item.tName || `#${item.idUsl}`,
        tov: null
      });
    }
    return opts;
  }, [tovOptions, isEdit, item]);

  const loadTov = (q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions(Array.isArray(data) ? data.map(normalizeTov) : []);
        })
        .catch(() => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions([]);
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  };

  useEffect(() => {
    setError("");
    setSotrLoading(true);
    fetchSprSotr()
      .then((data) => setSotrOptions(Array.isArray(data) ? data.map(normalizeSotr) : []))
      .catch(() => setSotrOptions([]))
      .finally(() => setSotrLoading(false));

    loadTov("");

    if (isEdit && item) {
      form.setFieldsValue({
        idUsl: item.idUsl ?? undefined,
        cnt: toNumber(item.cnt) ?? 0,
        zenaExp: toNumber(item.zenaExp) ?? 0,
        prcDiscount: toNumber(item.prcDiscount) ?? 0,
        idVrach: item.idVrach || undefined,
        idSotr: item.idSotr || undefined,
        vidPan: toNumber(item.vidPan) ?? 0,
        isPlat: (toNumber(item.isPlat) ?? 0) === 1,
        komment: item.komment ?? ""
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({
      cnt: 1,
      zenaExp: 0,
      prcDiscount: 0,
      vidPan: 0,
      isPlat: false,
      idSotr: userId || undefined,
      komment: ""
    });

    if (!idClient) return;
    fetchHealthClient(idClient)
      .then((card) => {
        const idVrach = toNumber(pick(card, "idSotr", "IdSotr"));
        const dateBeg = pick(card, "dateBeg", "DateBeg");
        const dateEnd = pick(card, "dateEnd", "DateEnd");
        const days = daysBetween(dateBeg, dateEnd);
        form.setFieldsValue({
          idVrach: idVrach || undefined,
          cnt: days != null && days > 0 ? days : 1
        });
      })
      .catch(() => {});
  }, [isEdit, item, idClient, userId, form]);

  const onTovChange = (idUsl, option) => {
    const tov = option?.tov;
    if (!tov || isEdit) return;
    form.setFieldsValue({
      zenaExp: tov.zenaExp ?? 0,
      vidPan: tov.vidPlan ?? 0,
      isPlat: (tov.isPlat ?? 0) === 1
    });
  };

  const onFinish = (values) => {
    if (!idClient) return;
    setSavingState(true);
    setError("");

    const body = {
      idUsl: values.idUsl,
      cnt: values.cnt ?? 0,
      zenaExp: values.zenaExp ?? 0,
      prcDiscount: values.prcDiscount ?? 0,
      idVrach: values.idVrach ?? 0,
      idSotr: values.idSotr ?? 0,
      unlimit: toNumber(item?.unlimit) ?? 0,
      vidPan: values.vidPan ?? 0,
      isPlat: values.isPlat ? 1 : 0,
      komment: values.komment ?? ""
    };

    const request = isEdit
      ? updateHealthClientUsl(idClient, { ...body, idStr: item.idStr })
      : createHealthClientUsl(idClient, body);

    request
      .then(() => {
        onSaved?.();
      })
      .catch((e) => {
        setError(e.message || "Не удалось сохранить назначение");
      })
      .finally(() => setSavingState(false));
  };

  return (
    <div className={`client-appointment-form${embedded ? " client-appointment-form--embedded" : ""}`}>
      {!embedded ? (
        <div className="client-appointment-form__heading">
          <div className="client-appointment-form__title">Назначение</div>
        </div>
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={error} className="client-appointment-form__error" />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        colon={false}
        disabled={saving}
        onFinish={onFinish}
        className="client-appointment-form__fields"
      >
        <Form.Item
          name="idUsl"
          label="Процедура"
          rules={[{ required: true, message: "Укажите процедуру" }]}
        >
          <Select
            showSearch
            allowClear={false}
            placeholder="Например: Электрофорез с лидазой на сустав"
            disabled={!canChangeUsl}
            filterOption={false}
            onSearch={loadTov}
            onChange={onTovChange}
            options={tovSelectOptions}
            loading={tovLoading}
            notFoundContent={tovLoading ? "Загрузка..." : "Нет данных"}
            popupMatchSelectWidth
          />
        </Form.Item>

        <div className="client-appointment-form__grid">
          <Form.Item
            name="cnt"
            label="Количество"
            rules={[{ required: true, message: "Укажите количество" }]}
          >
            <InputNumber min={0} className="client-appointment-form__number" />
          </Form.Item>
          <Form.Item name="zenaExp" label="Цена">
            <InputNumber min={0} step={0.01} disabled={!canEditPrice} className="client-appointment-form__number" />
          </Form.Item>
          <Form.Item name="prcDiscount" label="Скидка, %">
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              disabled={!canEditDiscount}
              className="client-appointment-form__number"
            />
          </Form.Item>
        </div>

        <Form.Item name="idVrach" label="Врач">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Не выбран"
            options={sotrSelectOptions}
            loading={sotrLoading}
          />
        </Form.Item>

        <Form.Item name="idSotr" label="Назначил">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Не выбран"
            options={sotrSelectOptions}
            loading={sotrLoading}
          />
        </Form.Item>

        <Form.Item name="vidPan" label="Вид планирования">
          <Select options={VID_PAN_OPTIONS} placeholder="Выберите режим" />
        </Form.Item>

        <Form.Item name="isPlat" valuePropName="checked" className="client-appointment-form__check">
          <Checkbox>Услуга платная</Checkbox>
        </Form.Item>

        <Form.Item name="komment" label="Примечание">
          <Input.TextArea
            rows={3}
            placeholder="Особые отметки для медицинского персонала..."
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        </Form.Item>

        {!embedded ? (
          <div className="client-appointment-form__actions">
            <Button type="primary" htmlType="submit" loading={saving}>
              ОК
            </Button>
            <Button onClick={onCancel} disabled={saving}>
              Отмена
            </Button>
          </div>
        ) : null}
      </Form>
    </div>
  );
};

export default ClientAppointmentForm;
